// Chinese zodiac: year animal + element (Lunar New Year boundary, the popular
// convention), inner animal from the solar-term month (Sun's longitude, as in
// the BaZi month pillar), secret animal from the double-hour in local solar
// time. Deterministic.

import * as Astronomy from "astronomy-engine";
import { CHINESE_ANIMALS, CHINESE_ELEMENTS, CHINESE_NEW_YEAR } from "../../components/star-charts/constants.js";
import { birthDataToUtc, bodyLongitude, mod360 } from "./core.js";

const BRANCHES = ["rat", "ox", "tiger", "rabbit", "dragon", "snake", "horse", "goat", "monkey", "rooster", "dog", "pig"];
const animalByKey = (k) => CHINESE_ANIMALS.find((a) => a.key === k);

/**
 * Lunar New Year [month, day] in China (UTC+8). The table (1920-2030) is
 * authoritative; outside it, the first new moon on or after Jan 21 agrees
 * with the real calendar ~98% of the time (leap-month years like 1985 differ).
 */
export function lunarNewYear(year) {
  if (CHINESE_NEW_YEAR[year]) return CHINESE_NEW_YEAR[year];
  const start = Astronomy.MakeTime(new Date(Date.UTC(year, 0, 20, 16, 0))); // Jan 21 00:00 CST
  const nm = Astronomy.SearchMoonPhase(0, start, 40);
  const cst = new Date(nm.date.getTime() + 8 * 3600000);
  return [cst.getUTCMonth() + 1, cst.getUTCDate()];
}

export function calculateChineseChart(birthData) {
  const { birthDate, hasBirthTime, birthLocation } = birthData;
  let y = birthDate.year;
  const [cnyMonth, cnyDay] = lunarNewYear(y);
  if (birthDate.month < cnyMonth || (birthDate.month === cnyMonth && birthDate.day < cnyDay)) y -= 1;

  // Year 4 CE was a Rat year; elements run in pairs over a 10-year cycle.
  const animal = CHINESE_ANIMALS[(((y - 4) % 12) + 12) % 12];
  const element = CHINESE_ELEMENTS[Math.floor(((((y - 4) % 10) + 10) % 10) / 2)];
  const polarity = y % 2 === 0 ? "Yang" : "Yin";

  // Month branch from the solar terms: the Tiger month begins at Li Chun,
  // when the Sun reaches 315°, and each month spans 30° of solar longitude.
  const utc = birthDataToUtc(birthData);
  const sunLon = bodyLongitude("sun", utc);
  const innerAnimal = animalByKey(BRANCHES[(2 + Math.floor(mod360(sunLon - 315) / 30)) % 12]);

  // Hour branch from local mean solar time (no DST, corrected for longitude
  // within the time zone). The Rat hour runs 23:00 to 01:00.
  let secretAnimal = null;
  if (hasBirthTime && birthLocation?.lng != null) {
    const solarHours = mod360(((utc.getUTCHours() + utc.getUTCMinutes() / 60 + birthLocation.lng / 15) * 15)) / 15;
    secretAnimal = animalByKey(BRANCHES[Math.floor(((solarHours + 1) % 24) / 2)]);
  }

  return { animal, element, polarity, innerAnimal, secretAnimal, adjustedYear: y, method: { year: "lunar-new-year", month: "solar-terms", hour: "local-solar-time" } };
}
